import type { AttentionItem, AttentionQueueResponse, MeResponse } from '@console/types'
import { useRef } from 'react'
import { Link, useNavigate, useOutletContext } from 'react-router-dom'
import { useLogoutMutation } from '../auth/queries.js'
import { formatAgeMs, formatAsOf, formatMoney, formatTrend } from '../format.js'
import { useAttentionQueue, useHomeAggregates } from '../home/queries.js'
import { useVerdicts, verdictLabel } from '../reference/queries.js'
import { useRuns } from '../runs/queries.js'
import { verdictStampVariant } from '../runs/verdict.js'
import { AsOfLine, Button, MetricCard, QueueItem, Skeleton, Stamp } from '../ui/index.js'
import styles from './HomePage.module.css'

/**
 * `LGH-031`. A genuine zero is good news — "none policy-held" — never
 * rendered as an absence of measurement the way a missing field would be.
 * `policyHeldCount` is always computable from the same rows `openCount`
 * already reads, so there is no separate "not measured" state to confuse it
 * with.
 */
function policyHeldSuffix(policyHeldCount: number): string {
  return policyHeldCount === 0 ? ' · none policy-held' : ` · ${String(policyHeldCount)} policy-held`
}

const ATTENTION_ACTION_LABEL: Record<AttentionItem['action'], string> = {
  review: 'Review',
  rule: 'Rule',
}

const ATTENTION_STAMP: Record<AttentionItem['reason'], 'stop' | 'you' | 'warn' | 'open'> = {
  mismatch: 'stop',
  ambiguity: 'you',
  approval_needed: 'warn',
  rule_needed: 'warn',
  // The only resolved reason in the queue. A signal colour would put it
  // ahead of items that still need someone.
  refused: 'open',
}

/**
 * Takes the whole response as a prop, narrowed once at the call site,
 * rather than each row re-reading a nullable `useQuery` result inside its
 * own closure — the same TS-narrowing-across-closures issue Run Detail's
 * `TriptychSection` worked around in Phase 4.
 */
function AttentionQueueSection({
  queue,
  onAction,
}: {
  queue: AttentionQueueResponse
  onAction: (item: AttentionItem) => void
}) {
  if (queue.totalCount === 0) {
    return <p className={styles['emptyQueue']}>Nothing needs you right now. Runs are flowing.</p>
  }

  const asOfMs = Date.parse(queue.asOf)

  return (
    <>
      {queue.items.slice(0, 5).map((item) => {
        const ageLabel = `${formatAgeMs(asOfMs - Date.parse(item.waitingSince))} ago`
        const amountLabel = formatMoney(item.amount)
        const detail =
          amountLabel === null
            ? `${item.detail} · ${ageLabel}`
            : `${item.detail} · ${amountLabel} · ${ageLabel}`

        return (
          <QueueItem
            key={item.runId}
            stamp={ATTENTION_STAMP[item.reason]}
            title={item.title}
            detail={detail}
            actionLabel={ATTENTION_ACTION_LABEL[item.action]}
            onAction={() => {
              onAction(item)
            }}
            policyFlags={item.policyFlags}
          />
        )
      })}
      {queue.unreadableItems > 0 && (
        // Same rule as the runs list: what was dropped is said, never inferred
        // from a shorter list. `current-state.md` §6.2 calls this queue "the
        // product", which makes a quietly incomplete one the worst version.
        <p role="status" className={styles['more']}>
          {queue.unreadableItems === 1
            ? '1 item could not be read by this console and is not shown.'
            : `${String(queue.unreadableItems)} items could not be read by this console and are not shown.`}
        </p>
      )}
      {queue.totalCount > 6 && (
        <p className={styles['more']}>
          {queue.totalCount - 5} more —{' '}
          <Link to="/runs?verdict=needs_you">see all in Runs → filter: needs you</Link>
        </p>
      )}
    </>
  )
}

/**
 * UI spec §2. All three sections are queries the console never assembles
 * itself (phase plan §0) — the four metrics, the attention queue's contents
 * *and* its order, and the five-run preview all come straight from Cadence.
 */
export function HomePage() {
  const me = useOutletContext<MeResponse>()
  const navigate = useNavigate()
  const logoutMutation = useLogoutMutation()
  const attentionRef = useRef<HTMLDivElement>(null)

  const aggregates = useHomeAggregates()
  const attention = useAttentionQueue(6)
  // Served vocabulary. While it is loading `verdictLabel` falls back to the
  // raw value, which is honest — an internal word beats an invented label.
  const verdicts = useVerdicts()
  const recent = useRuns({
    filter: {},
    sort: 'time',
    sortDirection: 'desc',
    offset: 0,
    limit: 5,
  })

  function scrollToAttention(): void {
    attentionRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  function sevenDaysAgoIso(): string {
    return new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()
  }

  function handleSignOut(): void {
    logoutMutation.mutate(undefined, {
      onSuccess: () => {
        navigate('/login', { replace: true })
      },
    })
  }

  const data = aggregates.data

  return (
    <main className={styles['page']}>
      <div className={styles['sectionHeader']}>
        <span>
          {/*
            The tenant name used to live here, served from the console's own
            tenants table. There is no such table now — Cadence is the registry
            — and the workspace is named by the switcher above, which reads it
            from there. This line is the operator, which is all it can honestly
            claim to know.
          */}
          <strong>{me.name}</strong> · {me.email}
        </span>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <Link to="/ask">Ask →</Link>
          <Link to="/health">Health →</Link>
          <Link to="/rfq-source-invites">Sources →</Link>
          <Link to="/onboarding-requests">Onboarding →</Link>
          <Link to="/demo-usage">Demo usage →</Link>
          <Button variant="secondary" onClick={handleSignOut} disabled={logoutMutation.isPending}>
            {logoutMutation.isPending ? 'Signing out…' : 'Sign out'}
          </Button>
        </div>
      </div>

      <div className={styles['sectionHeader']}>
        <h1 className={styles['sectionTitle']}>Home</h1>
        {data && (
          <AsOfLine
            asOf={data.asOf}
            onRefresh={() => {
              void aggregates.refetch()
              void attention.refetch()
              void recent.refetch()
            }}
          />
        )}
      </div>

      <section className={styles['metrics']}>
        {data ? (
          <>
            <MetricCard
              value={String(data.runsLast7Days.count)}
              label="Runs (7 day)"
              detail={
                formatMoney(data.runsLast7Days.totalValue) === null
                  ? formatTrend(data.runsLast7Days.trend, data.runsLast7Days.delta)
                  : `${formatTrend(data.runsLast7Days.trend, data.runsLast7Days.delta)} · ${formatMoney(data.runsLast7Days.totalValue) ?? ''} total`
              }
              onClick={() => {
                navigate(`/runs?dateFrom=${encodeURIComponent(sevenDaysAgoIso())}`)
              }}
            />
            <MetricCard
              value={
                data.autonomy.totalCount === 0
                  ? '—'
                  : `${String(Math.round((data.autonomy.autonomousCount / data.autonomy.totalCount) * 100))}%`
              }
              label="Autonomy"
              detail={`${String(data.autonomy.autonomousCount)}/${String(data.autonomy.totalCount)} · ${formatTrend(data.autonomy.trend, data.autonomy.delta, 'pp')}`}
              onClick={() => {
                navigate('/runs?verdict=needs_you')
              }}
            />
            <MetricCard
              value={String(data.attention.openCount)}
              label="Attention"
              detail={
                /* Two different absences, and they used to collapse into one
                   sentence. `openCount === 0` means nothing is waiting.
                   `oldestItemAgeMs === null` means we do not know how long the
                   oldest has waited — which real Cadence never reports. Saying
                   "nothing waiting" above a count of 11 is the console
                   contradicting itself on the same card. */
                data.attention.openCount === 0
                  ? 'nothing waiting'
                  : data.attention.oldestItemAgeMs === null
                    ? // "oldest not reported", not "age not reported" — `LGH-024`.
                      // The rows below this card each show their own age, so
                      // the old wording read as the console contradicting
                      // itself. What is missing is specifically the OLDEST
                      // across the whole queue: the aggregate does not carry
                      // it, and the console holds only the five items it
                      // renders, so computing it from those would report the
                      // oldest of five as the oldest of eleven.
                      `${String(data.attention.openCount)} waiting · oldest not reported${policyHeldSuffix(data.attention.policyHeldCount)}`
                    : `oldest: ${formatAgeMs(data.attention.oldestItemAgeMs)}${
                        formatMoney(data.attention.totalValue) === null
                          ? ''
                          : ` · ${formatMoney(data.attention.totalValue) ?? ''} pending`
                      }${policyHeldSuffix(data.attention.policyHeldCount)}`
              }
              onClick={scrollToAttention}
            />
            <MetricCard
              value={
                data.verified.totalCount === 0
                  ? '—'
                  : `${String(Math.round((data.verified.verifiedCount / data.verified.totalCount) * 100))}%`
              }
              label="Verified"
              detail={`${String(data.verified.verifiedCount)}/${String(data.verified.totalCount)} · ${formatTrend(data.verified.trend, data.verified.delta, 'pp')}`}
              onClick={() => {
                navigate('/runs?verdict=mismatch')
              }}
            />
          </>
        ) : (
          <>
            <Skeleton />
            <Skeleton />
            <Skeleton />
            <Skeleton />
          </>
        )}
      </section>

      <section className={styles['attention']} ref={attentionRef}>
        <div className={styles['sectionHeader']}>
          <h2 className={styles['sectionTitle']}>What needs you</h2>
          {attention.data && (
            <span className={styles['count']}>{attention.data.totalCount} items</span>
          )}
        </div>

        {attention.isPending && <Skeleton />}
        {attention.isError && (
          <p role="alert" style={{ color: 'var(--stop)' }}>
            {attention.error.message}
          </p>
        )}
        {attention.data && (
          <AttentionQueueSection
            queue={attention.data}
            onAction={(item) => {
              /*
               * ROUTED ON `reason`, NOT ON `action` — `LGH-007` Q4.
               *
               * `action: 'review'` also appears on `mismatch` and
               * `approval_needed` items, which belong on run detail with their
               * checks. Routing on it would send an operator investigating a
               * failed verification into a screen full of catalogue questions.
               * `ambiguity` is the drafter's own word for a field it had to ask
               * about, but it is NOT the review screen's signal — its own
               * `action` is `'rule'`, not `'review'` (`fake-server/
               * aggregates.ts`'s own `ACTION_BY_REASON`), and the button
               * beside this queue item already says "Rule →", read straight
               * from that same `action`. Sending it to `/review` anyway was a
               * real bug, found live (`REQ-design-the-operator-console-ux-
               * audit`, `18UX`, 2026-09-23): a customer-identity ambiguity
               * has no review draft, so the click landed on a guaranteed
               * `404` after several seconds of retries. `/runs/:id/rule`
               * exists and matches the button's own word — still disabled
               * there with its own honest "not available yet" copy
               * (`MutateRunPage`), which is a real, labelled stop rather
               * than a silent dead end. Where ambiguity resolution
               * eventually lives is a separate, open product question this
               * fix does not answer.
               */
              navigate(
                item.reason === 'ambiguity' ? `/runs/${item.runId}/rule` : `/runs/${item.runId}`,
              )
            }}
          />
        )}
      </section>

      <section className={styles['recent']}>
        <div className={styles['sectionHeader']}>
          <h2 className={styles['sectionTitle']}>Recent activity</h2>
          <Link to="/runs">See all runs →</Link>
        </div>

        {/* Only the rows are allowed to be clipped when the viewport is
            short — never the header above them. This section is a preview
            whose entire justification for giving way first is that
            "[See all runs →]" is right there as the escape hatch; clipping
            the escape hatch along with the preview is the one way to make
            that trade dishonest. Caught by `journey.spec.ts` at 1280×720
            (Playwright's Desktop Chrome default, and an ordinary laptop
            once browser chrome is subtracted): the link rendered and
            reported visible, but `<main>` swallowed every click on it. */}
        <div className={styles['recentRows']} data-testid="recent-rows">
          {recent.isPending && <Skeleton />}
          {recent.isError && (
            <p role="alert" style={{ color: 'var(--stop)' }}>
              {recent.error.message}
            </p>
          )}
          {recent.data?.rows.map((run) => (
            <div
              key={run.runId}
              className={styles['recentRow']}
              onClick={() => {
                navigate(`/runs/${run.runId}`)
              }}
            >
              <Stamp variant={verdictStampVariant(run.verdict)} />
              <span>{run.reference}</span>
              {/* The platform's own word for this verdict. This used to be
                  `run.verdict.replace('_', ' ')`, which authored a label out
                  of an internal value — and authored it wrong twice over:
                  `replace` with a string replaces only the FIRST match, so
                  `not_confirmed_yet` reached the operator as "not
                  confirmed_yet". Cadence serves "Not confirmed yet". */}
              <span style={{ color: 'var(--muted)' }}>
                {verdictLabel(verdicts.data, run.verdict)}
              </span>
              <span style={{ color: 'var(--muted)' }}>{run.currency ?? '—'}</span>
              {/*
                Operator-console UX audit finding, the headline one:
                `toLocaleTimeString()` reads the BROWSER'S local timezone with
                no label and no date — exactly the anti-pattern `format.ts`'s
                own standing rule exists to forbid ("nothing here calls
                toLocaleString without a zone"), and the exact class of bug
                `LGH-024` already found and fixed on `RunsPage`'s own Time
                column ("that rendered the browser's local time with NO
                label, in a table sitting beside as-of lines that say UTC").
                This preview of the SAME run list, one screen over, still had
                it. `formatAsOf` degrades to the full explicit instant
                (`formatInstant`) with no declared tenant calendar — the
                honest direction `DR-017`/`format.ts` require — rather than
                asserting "today" it cannot back up.
              */}
              <span style={{ color: 'var(--muted)' }}>{formatAsOf(run.occurredAt)}</span>
            </div>
          ))}
        </div>
      </section>
    </main>
  )
}
